import { useEffect, useRef, useState, type ReactNode } from "react";
import { PhoneIcon, SparklesIcon } from "lucide-react";

import { CandidacyJobBanner } from "./CandidacyJobBanner";
import { CandidateSheetActionsContainer } from "./CandidateSheetActionsContainer";
import { CandidateSheetMoreActionsMenu } from "./CandidateSheetMoreActionsMenu";
import { CandidateSheetCommsPanel } from "@jobs/candidate-sheet-v2/comms/CandidateSheetCommsPanel";
import { CreateResumeFromProfileButton } from "./CreateResumeFromProfileButton";
import { EditUserProfileDialog } from "./EditUserProfileDialog";
import { EnrichmentPortal } from "./EnrichmentPortal";
import { EvaluationsTab } from "@jobs/candidate-sheet-v2/evaluations/EvaluationsTab";
import { OldEvaluationsTab } from "@jobs/candidate-sheet-v2/evaluations-old/OldEvaluationsTab";
import { AddExecutiveSummaryButton } from "@jobs/candidate-sheet-v2/exec-summary/AddExecutiveSummaryButton";
import { ExecutiveSummarySection } from "@jobs/candidate-sheet-v2/exec-summary/ExecutiveSummarySection";
import { NotesTab } from "@jobs/candidate-sheet-v2/notes/NotesTab";
import { StatusChangeDialogs } from "@jobs/candidate-sheet-v2/status-change-dialogs/StatusChangeDialogs";
import { Column, ConfirmationDialog, Row } from "@/components";
import { Button } from "@/components/ui/button";
import { ReadOnlyCommsPanel } from "@/components/user-sheet/ReadOnlyCommsPanel";
import { UserSheetActivityRail } from "@/components/user-sheet/UserSheetActivityRail";
import { UserSheetBody } from "@/components/user-sheet/UserSheetBody";
import { UserSheetFrame } from "@/components/user-sheet/UserSheetFrame";
import { UserSheetHeader } from "@/components/user-sheet/UserSheetHeader";
import { UserSheetHeaderDetails } from "@/components/user-sheet/UserSheetHeaderDetails";
import { UserSheetProvider } from "@/components/user-sheet/UserSheetProvider";
import { UserSheetTabs } from "@/components/user-sheet/UserSheetTabs";
import { CandidacyTab } from "@jobs/candidate-sheet-v2/tabs/CandidacyTab";
import { InterviewsTab } from "@jobs/candidate-sheet-v2/tabs/InterviewsTab";
import { PlacementsTab } from "@jobs/candidate-sheet-v2/tabs/PlacementsTab";
import { StevieCallsTab } from "@jobs/candidate-sheet-v2/tabs/StevieCallsTab";
import { OtherCandidaciesTab } from "@/components/user-sheet/tabs/OtherCandidaciesTab";
import { ProfileTab } from "@/components/user-sheet/tabs/ProfileTab";
import { RecommendedJobsTab } from "@/components/user-sheet/tabs/RecommendedJobsTab";
import { CandidacyRecordsTab } from "@jobs/candidate-sheet-v2/records/CandidacyRecordsTab";

import { SheetScopeProvider } from "@/contexts/SheetScopeContext";
import { useUnreadNotifications } from "@/contexts/UnreadNotificationsContext";
import { useErrorToast } from "@/hooks/useErrorToast";
import { useSelectedTabFromUrl } from "@/hooks/useSelectedTabFromUrl";
import { useCreateAddCandidacyArtifacts } from "@/hooks/createAdds/useCreateAddCandidacyArtifacts";
import { useGetCallHistory } from "@/hooks/queries/useGetCallHistory";
import { useGetCandidacyEvaluationNew } from "@/hooks/queries/useGetCandidacyEvaluationNew";
import { useGetCandidacyInfo } from "@/hooks/queries/useGetCandidacyInfo";
import { useGetCandidateSheetV2TabCounts } from "@/hooks/queries/useGetCandidateSheetV2TabCounts";
import { useGetJobInternalConfig } from "@/hooks/queries/useGetJobInternalConfig";
import { useGetPlacements } from "@/hooks/queries/useGetPlacements";
import { useGetCachedCompaniesSummary } from "@/hooks/sets/useGetCachedCompaniesSummary";
import { useUpdateSetNotificationsReadByCandidacy } from "@/hooks/updateSets/useUpdateSetNotificationsReadByCandidacy";
import { candidacyEvaluations } from "@/lib/candidacyEvaluations";
import { defaultPlacementQueryParams } from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { getCandidacyLink } from "@/lib/notificationLinks";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { cn } from "@/lib/utils";
import {
  CandidacyStatus,
  CandidateSheetVariant,
  Job,
  NotificationType,
  VoiceAIOutreachStatus,
} from "@/types";
import { WorkedAtCustomerBanner } from "@/components/user-sheet/WorkedAtCustomerBanner";

enum CandidateSheetV2Tab {
  CANDIDACY = "candidacy",
  EVALUATION = "evaluation",
  EVALUATION_OLD = "evaluationOld",
  INTERVIEWS = "interviews",
  NOTES = "notes",
  OTHER_CANDIDACIES = "other",
  PLACEMENTS = "placements",
  PROFILE = "profile",
  RECOMMENDED_JOBS = "recommended",
  RECORDS = "records",
  STEVIE_CALLS = "stevie",
}

// Candidate-sheet V2 composes the shared user-sheet blocks (provider, frame,
// body, header, tabs) and owns everything candidacy-specific: tabs, status
// dialogs, enrichment, and the sheet chrome.
export const CandidateSheetV2 = ({
  candidacyId,
  canMoveToNextCandidacy,
  canMoveToPreviousCandidacy,
  currentCandidacyIndex,
  fallbackName,
  handleMoveToNextCandidacy,
  handleMoveToPreviousCandidacy,
  job,
  onClose,
  recordsTabIndicator,
  topBarAction,
  totalCandidaciesCount,
  variant = CandidateSheetVariant.SHEET,
  workerId,
}: {
  candidacyId: string;
  canMoveToNextCandidacy: boolean;
  canMoveToPreviousCandidacy: boolean;
  currentCandidacyIndex?: number;
  fallbackName?: string;
  handleMoveToNextCandidacy: () => void;
  handleMoveToPreviousCandidacy: () => void;
  job: Job;
  onClose: () => void;
  recordsTabIndicator?: ReactNode;
  topBarAction?: ReactNode;
  totalCandidaciesCount?: number;
  variant?: CandidateSheetVariant;
  workerId: number;
}) => {
  /* Database CRUD Operations */
  // Read
  const candidacyInfoQuery = useGetCandidacyInfo({ candidacyId });
  const candidacyInfo = candidacyInfoQuery.data;
  const callHistoryQuery = useGetCallHistory(workerId);
  const placementsQuery = useGetPlacements({
    jobId: job.id,
    queryOptions: defaultPlacementQueryParams,
  });
  const { data: tabCounts } = useGetCandidateSheetV2TabCounts({ candidacyId });
  const { data: evaluation } = useGetCandidacyEvaluationNew({ candidacyId });
  const { data: jobInternalConfig } = useGetJobInternalConfig();
  // Create
  const {
    mutate: generateCandidacyArtifacts,
    isPending: isGeneratingArtifacts,
  } = useCreateAddCandidacyArtifacts();
  // Update
  const { mutate: markNotificationsReadByCandidacy } =
    useUpdateSetNotificationsReadByCandidacy();
  const companies = useGetCachedCompaniesSummary();
  const companyName =
    companies.find((c) => c.id === job.companyId)?.name ?? "Company";

  /* React Hooks */
  const { unreadCountsByType } = useUnreadNotifications();
  // No unread mentions anywhere means there is nothing to clear for this
  // candidacy either, so the sheet opens without a request.
  const hasUnreadMentions = [
    NotificationType.INTERNAL_COMMENT_MENTION,
    NotificationType.THREAD_REPLY,
  ].some((type) => Boolean(unreadCountsByType[type]));
  const errorToast = useErrorToast();
  // useState
  // URL-backed so a link can open the sheet on a specific tab — "st" (sheet tab)
  // rather than "t", which the job page underneath already owns.
  const { setTabValue: setActiveTab, tabValue: activeTab } =
    useSelectedTabFromUrl({
      defaultValue: CandidateSheetV2Tab.CANDIDACY,
      paramName: "st",
    });
  const [isEditProfileDialogOpen, setIsEditProfileDialogOpen] = useState(false);
  const [isEditingExecSummary, setIsEditingExecSummary] = useState(false);
  const [isEnrichDialogOpen, setIsEnrichDialogOpen] = useState(false);
  const [isEnrichmentPortalOpen, setIsEnrichmentPortalOpen] = useState(false);
  // Latches on first open and stays on for the rest of this candidate, keeping
  // the portal mounted behind the profile so its chat survives an overwrite or
  // a trip back.
  const [hasOpenedEnrichmentPortal, setHasOpenedEnrichmentPortal] =
    useState(false);
  const [openStatusChangeDialog, setOpenStatusChangeDialog] = useState<
    CandidacyStatus | undefined
  >(undefined);
  // Stepping to another candidacy resets the tab. Reviewers keep their tab
  // across the queue, which is why the review portal opts out. Skipped on the
  // first render: the tab now lives in the URL, and resetting on mount would
  // wipe an ?st= deep link before it ever showed. The ref guard also makes this
  // idempotent, so setActiveTab being a fresh identity each render is harmless.
  const previousCandidacyIdRef = useRef<string | null>(null);
  useEffect(() => {
    const previousCandidacyId = previousCandidacyIdRef.current;
    previousCandidacyIdRef.current = candidacyId;
    if (variant === CandidateSheetVariant.EMBEDDED) return;
    if (previousCandidacyId === null || previousCandidacyId === candidacyId) {
      return;
    }
    setActiveTab(CandidateSheetV2Tab.CANDIDACY);
  }, [candidacyId, setActiveTab, variant]);

  // Enrichment belongs to the candidate it was opened for. The latch would
  // otherwise stay on for the rest of the session, mounting a hidden portal
  // behind every candidate after it and running its calls and Stevie-outreach
  // queries for people nobody asked to enrich.
  useEffect(() => {
    setIsEnrichDialogOpen(false);
    setIsEnrichmentPortalOpen(false);
    setHasOpenedEnrichmentPortal(false);
  }, [candidacyId]);

  // Opening the sheet counts as reading the mentions that named the staffer on
  // this candidacy. The server picks the rows; the ref keeps it to one call per
  // candidacy.
  const markedReadCandidacyIdRef = useRef<string | null>(null);
  useEffect(() => {
    if (markedReadCandidacyIdRef.current === candidacyId) return;
    if (!hasUnreadMentions) return;
    markedReadCandidacyIdRef.current = candidacyId;
    markNotificationsReadByCandidacy(candidacyId);
  }, [candidacyId, hasUnreadMentions, markNotificationsReadByCandidacy]);

  /* Helpers */
  // The review portal supplies its own dialog, decision buttons, and queue
  // navigation, so the sheet drops its frame and header chrome there.
  const isEmbedded = variant === CandidateSheetVariant.EMBEDDED;
  // Every job keyword highlights on the sheet — search keywords and
  // highlight-only ones alike; the color comes from the keyword's category.
  const highlightKeywords = job.keywords.map(
    ({ category, color, keyword }) => ({ category, color, keyword }),
  );
  const isEnrichAndCheck =
    candidacyInfo?.status === CandidacyStatus.ENRICHANDCHECK;
  const latestAgenticScore = candidacyInfo?.agenticScore;
  const placements =
    placementsQuery.data?.filter(
      (placement) => placement.candidacy.id === candidacyId,
    ) ?? [];
  const notesCount = tabCounts?.note ?? 0;
  const stevieCallsCount = (callHistoryQuery.data?.aiCalls ?? []).filter(
    (call) =>
      call.candidacyId === candidacyId &&
      call.status === VoiceAIOutreachStatus.COMPLETED.toString(),
  ).length;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing
  const candidateFirstName = fallbackName?.split(" ")[0] || "Candidate";
  const hasCallRecords =
    (callHistoryQuery.data?.aiCalls.length ?? 0) > 0 ||
    (callHistoryQuery.data?.stafferCalls.length ?? 0) > 0;
  const resolvedRecordsTabIndicator =
    recordsTabIndicator ??
    (hasCallRecords ? (
      <PhoneIcon
        className="h-3.5 w-3.5 shrink-0 text-slate-600"
        strokeWidth={2}
      />
    ) : undefined);
  // The backend count only covers saved followups; recommended followups are
  // computed client-side, so fold them in (deduped by title like the editor).
  const followUps = Array.isArray(evaluation?.followUps)
    ? evaluation.followUps
    : [];
  const recommendedFollowUpsCount = [
    candidacyEvaluations.getCommuteRecommendation({
      commuteDistanceMiles: candidacyInfo?.commuteDistanceMiles,
      commuteTimeMins: candidacyInfo?.commuteTimeMins,
    }),
  ]
    .filter(Boolean)
    .filter(
      (recommendation) =>
        !followUps.some((followUp) => followUp.title === recommendation?.title),
    ).length;
  const evaluationCount =
    (tabCounts?.evaluation ?? 0) + recommendedFollowUpsCount;
  // Jobs still configured with evaluation builds run the legacy evaluations
  // tab; the V2 evaluation drops into the more dropdown behind it.
  const hasLegacyEvaluations = !!jobInternalConfig?.evaluations;

  /* Handlers */
  const handleCreateResumeFromProfile = () => {
    if (!candidacyInfo) return;

    mixpanelUtils.trackButtonClick(
      "Generate Resume from Profile",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    // The hook owns cache invalidation and the primary-resume update.
    generateCandidacyArtifacts(
      { candidacyId, talentId: candidacyInfo.talentId ?? null },
      {
        onSuccess: () => {
          setActiveTab(CandidateSheetV2Tab.RECORDS);
        },
        onError: (error) => {
          errorToast({
            description:
              error.message || "Failed to create resume from profile.",
          });
        },
      },
    );
  };

  const handleOpenEnrichmentPortal = () => {
    mixpanelUtils.trackButtonClick(
      "Enrich Profile",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    setIsEnrichDialogOpen(false);
    setIsEnrichmentPortalOpen(true);
    setHasOpenedEnrichmentPortal(true);
  };

  /* Render */
  const renderEnrichAction = () => {
    // Enrichment overwrites the profile — not something to do mid-review.
    if (!isEnrichAndCheck || isEmbedded) return null;

    return (
      <Button
        className="h-7 gap-1.5 px-2.5"
        onClick={handleOpenEnrichmentPortal}
        size="sm"
      >
        <SparklesIcon className="h-3.5 w-3.5" />
        Enrich
      </Button>
    );
  };

  const renderProfessionalSummaryActions = () => {
    // Reviewers neither regenerate resumes nor write summaries mid-review.
    if (isEmbedded) return null;

    return (
      <Row className="items-center gap-1">
        {renderAddExecSummaryAction()}
        <CreateResumeFromProfileButton
          isPending={isGeneratingArtifacts}
          onClick={handleCreateResumeFromProfile}
        />
      </Row>
    );
  };

  const renderExecSummarySection = () => {
    if (!candidacyInfo) return null;

    return (
      <ExecutiveSummarySection
        candidacyInfo={candidacyInfo}
        isEditing={isEditingExecSummary}
        setIsEditing={setIsEditingExecSummary}
      />
    );
  };

  const renderAddExecSummaryAction = () => {
    // Once a summary exists the section owns the edit affordance, and reviewers
    // never write one.
    if (
      isEmbedded ||
      isEditingExecSummary ||
      !candidacyInfo ||
      candidacyInfo.summary
    ) {
      return null;
    }

    return (
      <AddExecutiveSummaryButton
        onClick={() => {
          setIsEditingExecSummary(true);
        }}
      />
    );
  };

  const renderTabs = () => {
    return (
      <UserSheetTabs
        activeTab={activeTab}
        // Remounting per candidate would throw away the kept-previous data the
        // review queue relies on, and reviewers want to stay on one tab anyway.
        key={isEmbedded ? undefined : candidacyId}
        onActiveTabChange={setActiveTab}
        scrollHeader={
          <UserSheetHeaderDetails
            metricValues={{
              commuteMinutes: candidacyInfo?.commuteTimeMins,
              lastAiCommunication: candidacyInfo?.lastAiCommunication,
              lastHumanCommunication: candidacyInfo?.lastHumanCommunication,
            }}
          />
        }
        stickyOffsetClassName={isEmbedded ? "-top-px" : undefined}
        tabBarActions={renderEnrichAction()}
        tabs={[
          {
            content: (
              <CandidacyTab
                highlightKeywords={highlightKeywords}
                onOpenStevieCalls={() => {
                  setActiveTab(CandidateSheetV2Tab.STEVIE_CALLS);
                }}
                onOpenSheetTab={(tab) => {
                  setActiveTab(
                    tab === "notes"
                      ? CandidateSheetV2Tab.NOTES
                      : CandidateSheetV2Tab.RECORDS,
                  );
                }}
              />
            ),
            label: "Candidacy",
            value: CandidateSheetV2Tab.CANDIDACY,
          },
          {
            content: (
              <ProfileTab
                execSummary={renderExecSummarySection()}
                highlightKeywords={highlightKeywords}
                isEditable={!isEmbedded}
                professionalSummaryAction={renderProfessionalSummaryActions()}
                rail={
                  <UserSheetActivityRail
                    pipelines={candidacyInfo?.activePipelines ?? []}
                    recentActivityAt={candidacyInfo?.recentActivityAt}
                    submissions={candidacyInfo?.submissions ?? []}
                  />
                }
                showProfessionalSummary
              />
            ),
            label: "Profile",
            value: CandidateSheetV2Tab.PROFILE,
          },
          {
            content: <StevieCallsTab candidateFirstName={candidateFirstName} />,
            count: stevieCallsCount,
            label: "Stevie",
            value: CandidateSheetV2Tab.STEVIE_CALLS,
          },
          {
            content: <CandidacyRecordsTab />,
            count: tabCounts?.record,
            indicator: resolvedRecordsTabIndicator,
            label: "Records",
            value: CandidateSheetV2Tab.RECORDS,
          },
          {
            content: <OtherCandidaciesTab />,
            count: tabCounts?.otherCandidacy,
            label: "Other Candidacies",
            value: CandidateSheetV2Tab.OTHER_CANDIDACIES,
          },
          {
            content: <RecommendedJobsTab />,
            label: "Recommended Jobs",
            value: CandidateSheetV2Tab.RECOMMENDED_JOBS,
          },
          {
            content: (
              <EvaluationsTab
                candidacyId={candidacyInfo?.id}
                isError={candidacyInfoQuery.isError}
                isPending={candidacyInfoQuery.isPending}
                talentId={workerId}
              />
            ),
            count: evaluationCount || undefined,
            isMoreTab: hasLegacyEvaluations,
            label: "Evaluation",
            value: CandidateSheetV2Tab.EVALUATION,
          },
          ...(hasLegacyEvaluations
            ? [
                {
                  content: <OldEvaluationsTab candidacyId={candidacyId} />,
                  label: "Evaluations (Old)",
                  value: CandidateSheetV2Tab.EVALUATION_OLD,
                },
              ]
            : []),
          {
            content: (
              <NotesTab
                companyName={companyName}
                jobTitle={job.title}
                userId={workerId}
              />
            ),
            count: notesCount,
            isMoreTab: notesCount < 1,
            label: "Notes",
            value: CandidateSheetV2Tab.NOTES,
          },
          {
            content: (
              <InterviewsTab isError={false} isPending={false} job={job} />
            ),
            isMoreTab: true,
            label: "Interviews",
            value: CandidateSheetV2Tab.INTERVIEWS,
          },
          {
            content: (
              <PlacementsTab
                isError={placementsQuery.isError}
                isPending={placementsQuery.isPending}
                job={job}
                placements={placements}
              />
            ),
            count: placements.length,
            isMoreTab: true,
            label: "Placements",
            value: CandidateSheetV2Tab.PLACEMENTS,
          },
        ]}
      />
    );
  };

  const renderHeader = () => {
    return (
      <UserSheetHeader
        actions={
          isEmbedded ? undefined : (
            <CandidateSheetActionsContainer
              key={candidacyId}
              canMoveToNextCandidacy={canMoveToNextCandidacy}
              canMoveToPreviousCandidacy={canMoveToPreviousCandidacy}
              currentCandidacyIndex={currentCandidacyIndex ?? 0}
              handleMoveToNextCandidacy={handleMoveToNextCandidacy}
              handleMoveToPreviousCandidacy={handleMoveToPreviousCandidacy}
              onStatusChanged={(newStatus: CandidacyStatus) => {
                // Moving into Enrich and Check prompts the enrichment dialog.
                if (newStatus === CandidacyStatus.ENRICHANDCHECK) {
                  setIsEnrichDialogOpen(true);
                }
              }}
              setOpenStatusChangeDialog={setOpenStatusChangeDialog}
              totalCandidaciesCount={totalCandidaciesCount ?? 1}
            />
          )
        }
        agenticScore={latestAgenticScore}
        fallbackName={fallbackName}
        // The portal hides the status controls, so give reviewers a way out to
        // the full sheet that doesn't tear down the review queue.
        nameLinkTo={
          isEmbedded
            ? getCandidacyLink({
                candidacyId,
                companyId: job.companyId,
                jobId: job.id,
              })
            : undefined
        }
        moreActions={
          isEmbedded ? undefined : (
            <CandidateSheetMoreActionsMenu
              key={candidacyId}
              onDeleted={onClose}
            />
          )
        }
      />
    );
  };

  const renderBody = () => {
    return (
      <UserSheetBody
        // Reviewers read the thread but never reach out from the portal.
        commsPanel={
          isEmbedded ? (
            <ReadOnlyCommsPanel key={candidacyId} />
          ) : (
            <CandidateSheetCommsPanel key={candidacyId} />
          )
        }
      >
        {hasOpenedEnrichmentPortal ? (
          <Column
            className={cn(
              "min-h-0 flex-1 overflow-y-auto",
              !isEnrichmentPortalOpen && "hidden",
            )}
          >
            <Column className="min-h-0 flex-1 p-6">
              <EnrichmentPortal
                candidacyId={candidacyId}
                key={candidacyId}
                onApplied={() => {
                  setIsEnrichmentPortalOpen(false);
                }}
                onExit={() => {
                  setIsEnrichmentPortalOpen(false);
                }}
              />
            </Column>
          </Column>
        ) : null}
        {isEnrichmentPortalOpen ? null : (
          <>
            {renderHeader()}
            {renderTabs()}
          </>
        )}
      </UserSheetBody>
    );
  };

  const renderSheet = () => {
    // The review portal supplies its own dialog frame, so the sheet mounts the
    // body bare there instead of its own overlay.
    if (isEmbedded) return renderBody();

    return (
      <UserSheetFrame fallbackName={fallbackName} onClose={onClose}>
        <div className="shrink-0">
          <CandidacyJobBanner action={topBarAction} />
        </div>
        <WorkedAtCustomerBanner
          isCurrentLaborupPlacement={!!candidacyInfo?.currentPlacement}
        />
        {renderBody()}
      </UserSheetFrame>
    );
  };

  const render = () => {
    return (
      // The legacy status/enrichment components (status-change dialogs, the
      // enrichment portal) read job + companyName from this context.
      <SheetScopeProvider
        candidacyId={candidacyId}
        companyId={job.companyId}
        companyName={companyName}
        job={job}
        jobId={job.id}
      >
        <UserSheetProvider
          candidacyId={candidacyId}
          // The portal's own layout is already tight; the rail starts collapsed there.
          defaultCommunicationsOpen={!isEmbedded}
          // Reviewers decide on the candidate as submitted — no edits mid-review.
          isReadOnly={isEmbedded}
          jobId={job.id}
          onEditProfile={
            isEmbedded
              ? undefined
              : () => {
                  setIsEditProfileDialogOpen(true);
                }
          }
          workerId={workerId}
        >
          {renderSheet()}
        </UserSheetProvider>
        {candidacyInfo ? (
          <EditUserProfileDialog
            key={candidacyId}
            onOpenChange={setIsEditProfileDialogOpen}
            onSuccess={() => {
              setIsEditProfileDialogOpen(false);
            }}
            open={isEditProfileDialogOpen}
            userId={workerId}
          />
        ) : null}
        <ConfirmationDialog
          cancelButtonText="Not now"
          confirmButtonText="Enrich"
          description="Would you like to enrich this candidate's profile?"
          onConfirm={handleOpenEnrichmentPortal}
          onOpenChange={setIsEnrichDialogOpen}
          open={isEnrichDialogOpen}
          title="Enrich candidate profile?"
          variant="square"
        />
        <StatusChangeDialogs
          openStatusChangeDialog={openStatusChangeDialog}
          setOpenStatusChangeDialog={setOpenStatusChangeDialog}
        />
      </SheetScopeProvider>
    );
  };

  return render();
};
